import { useState, type FormEvent } from "react";
import type { Me } from "@ai-rpg/protocol";
import { signIn } from "../api.ts";

const problemOf = (err: unknown) => (err instanceof Error ? err.message : String(err));

interface Props {
  readonly onSignedIn: (me: Me) => void;
}

/** What someone with no session sees: the name of the place, and where to say who they are. */
export function SignIn({ onSignedIn }: Props) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      onSignedIn(await signIn(username, password));
    } catch (err) {
      setProblem(problemOf(err));
      setPassword("");
      setBusy(false);
    }
  };

  return (
    <main className="signin">
      <p className="signin-place">The Table</p>
      <h1 className="lobby-title">Sign in</h1>
      <p className="lobby-lede">
        Your characters and your games are kept under your name. Accounts are made by whoever runs this table: if you
        have none, or have lost your password, ask them.
      </p>
      <p className="field-note">
        New here? Read <a href="/guide">the guide</a> to see how it plays, or <a href="/roadmap">the roadmap</a> for
        what is here and what is coming.
      </p>
      <form className="form" onSubmit={submit}>
        <label className="field">
          <span className="field-label">Username</span>
          <input
            className="input"
            name="username"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">Password</span>
          <input
            className="input"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <button className="button button-primary" type="submit" disabled={busy || !username || !password}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        {problem && (
          <p className="problem" role="alert">
            {problem}
          </p>
        )}
      </form>
    </main>
  );
}
